<script>
import ChannelSelectGroup from '@/components/watchmen/channels/ChannelSelectGroup';
import Button2 from '@/components/ui/Button2';
import { FormControl, FormLabel, FormElement, FormWrapper, FormError } from '@/components/ui/forms';
import UiInput from '@/components/ui/forms/UiInput';
import { BFormCheckboxGroup, BFormCheckbox } from 'bootstrap-vue';
import UiCard from '@/components/ui/Card';

export default {
  name: "ShiftCard",
  components: {
    ChannelSelectGroup,
    Button2,
    FormControl,
    FormLabel,
    FormElement,
    FormWrapper,
    FormError,
    UiInput,
    BFormCheckboxGroup,
    BFormCheckbox,
    UiCard,
},
  props: {
    value: {
      type: Object,
    },
    proLicences: {
      type: Boolean
    }
  },
  data() {
    return {
      shift: this.formatInitialData(this.value),
    };
  },
  methods: {
    dayName: function (day) {
      return [
        window.V.$i18n.t("global.sunday"), 
        window.V.$i18n.t("global.monday"),
        window.V.$i18n.t("global.tuesday"),
        window.V.$i18n.t("global.wednesday"),
        window.V.$i18n.t("global.thursday"),
        window.V.$i18n.t("global.friday"),
        window.V.$i18n.t("global.saturday")
      ][day]
    },
    removeSeconds: function (time) {
      if (!!time) {
        const timeSplit = time.split(':')
        return timeSplit[0] + ':' + timeSplit[1]
      }
      return time
    },
    formatInitialData(data) {
      const { time_start, time_end } = data;
      return {
        channels: {
          email: [],
          sms: [],
          tts: []
        },
        ...data,
        time_start: this.removeSeconds(time_start),
        time_end: this.removeSeconds(time_end),
      }
    }
  },
  computed: {
    europeanWeek() {
      return [1,2,3,4,5,6,0]
    },
    validDates() {
      const start = this.shift.date_start;
      const end = this.shift.date_end;
      if (start && end) {
        return new Date(start + ` ${this.shift.time_start}`) <= new Date(end + ` ${this.shift.time_end}`)
      } else {
        return true
      }
    },
    timeDifference() {
      function twoDigitNumber (num) {
        return num < 10 ? '0' + num : num
      }
      if (!this.shift.time_start || !this.shift.time_end ) {
        return ''
      }
      const start = this.shift.time_start.split(':');
      const end = this.shift.time_end.split(':')
      let hours = end[0] - start[0]
      let minutes = end[1] - start[1]
      if (minutes < 0) {
        minutes += 60
        hours -= 1
      }
      if (hours < 0) {
        hours += 24
      }
      if (minutes === 0 && hours === 0) {
        return '24h00'
      } 
      return twoDigitNumber(hours) + 'h' + twoDigitNumber(minutes)
    },
  },
  watch: {
    value: {
      handler(newVal) {
        this.shift = newVal;
      },
      deep: true
    },
    shift: {
      handler(newVal) {
        this.$emit("input", newVal);
      },
      deep: true
    }
  },
}
</script>

<template>
  <div class="row">
    <div class="col-lg-6">
      <FormWrapper>
        <FormControl>
          <FormLabel>{{ $t('watches.name') }}</FormLabel>
          <UiInput v-model="shift.name" />
        </FormControl>
        <FormControl>
          <FormElement>
            <BFormCheckbox 
              v-model="shift.active" 
              name="check-button" 
              switch 
              class="ml-2" 
              :aria-label="$t('watches.active_shift')"
            >
              <strong v-if="shift.active">{{ $t('watches.activated') }}</strong>
              <span v-else>{{ $t('watches.activate') }}</span>
            </BFormCheckbox>
          </FormElement>
        </FormControl>
        <FormControl>
          <FormLabel>
            {{ $t('watches.label_days') }}
          </FormLabel>
          <FormElement>
            <BFormCheckboxGroup v-model="shift.days" stacked>
              <BFormCheckbox v-for="day in europeanWeek" :key="day" :value="day" >
                {{ dayName(day) }}
              </BFormCheckbox>
            </BFormCheckboxGroup>
          </FormElement>
          <FormError v-if="!shift.days.length">
            {{ $t('watches.error_days') }}
          </FormError>
        </FormControl>
        <FormControl>
          <FormLabel>
            {{ $t('watches.label_time') }}
          </FormLabel>
          <FormElement>
            <div class="d-flex flex-wrap">
              <div class="flex-fill">
                <UiInput v-model="shift.time_start" type="time" style="margin-right: -1px" />
              </div>
              <div class="flex-fill">
                <UiInput v-model="shift.time_end" type="time" style="margin-left: -1px" />
              </div>
              <div
                class="text-mono ml-2"
                :style="{ 
                  color: 'var(--colors-gray-500)',
                  lineHeight: 'var(--input-height)',
                }"
              >
                {{ timeDifference }}
              </div>
            </div>
          </FormElement>
        </FormControl>
        <FormControl>
          <BFormCheckbox v-model="shift.dates_active">
            <strong>{{ $t('watches.active_dates') }}</strong>
          </BFormCheckbox>
        </FormControl>
        <FormControl v-if="shift.dates_active">
          <FormLabel>
            {{ $t('watches.label_date') }}
          </FormLabel>
          <FormElement>
            <div class="d-flex">
              <UiInput v-model="shift.date_start" type="date" style="margin-right: -1px" :class="!validDates && 'is-invalid'" />
              <UiInput v-model="shift.date_end" type="date" style="margin-left: -1px" :class="!validDates && 'is-invalid'" />
            </div>
          </FormElement>
          <FormError v-if="!validDates">
            {{ $t('watches.error_date') }}
          </FormError>
        </FormControl>
      </FormWrapper>
    </div>
    <div class="col-lg-6">
      <ChannelSelectGroup v-model="shift.channels" :proLicences="proLicences" />
    </div>
  </div>
</template>


<style lang="scss">
.ghost {
  opacity: 0.67;
  background-color: var(--colors-gray-100) !important;
}
.handle {
  cursor: move;
}
</style>